import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Search as SearchIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  TextFields as TextFieldsIcon,
  Numbers as NumbersIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  NotesOutlined as NotesIcon,
  Today as TodayIcon,
  Title as TitleIcon,
  Subtitles as SubtitlesIcon,
  Functions as FunctionsIcon,
  AddCircleOutline as ExtraFieldIcon,
} from "@mui/icons-material";
import { startCase } from "lodash";
import { cleanupMultilineConnectionsAfterRetag } from "../../../../../../utils/textFieldConnections";

export const GENERIC_FORM_FIELDS = [
  {
    field_id: "generic_text",
    field_label: "Text",
    field_type: "text",
    type: "PDFTextField",
    description: "Reusable generic single-line text input",
    icon: <TextFieldsIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_number",
    field_label: "Number",
    field_type: "number",
    type: "PDFTextField",
    description: "Reusable generic numeric input",
    icon: <NumbersIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_dollar",
    field_label: "Dollar",
    field_type: "dollar",
    type: "PDFTextField",
    description: "Reusable currency and dollar amount field",
    icon: <AttachMoneyIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_date",
    field_label: "Date",
    field_type: "date",
    type: "PDFTextField",
    description: "Reusable generic date field",
    icon: <CalendarTodayIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_multiline",
    field_label: "Multiline",
    field_type: "multiline",
    type: "PDFTextField",
    description: "Reusable multi-line text area",
    icon: <NotesIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_email",
    field_label: "Email",
    field_type: "email",
    type: "PDFTextField",
    description: "Reusable generic email field",
    icon: <EmailIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_phone",
    field_label: "Phone",
    field_type: "mobile",
    type: "PDFTextField",
    description: "Reusable generic phone / mobile number",
    icon: <PhoneIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_money",
    field_label: "Money",
    field_type: "money",
    type: "PDFTextField",
    description: "Reusable generic monetary value field",
    icon: <AttachMoneyIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_todaydate",
    field_label: "Today Date",
    field_type: "todayDate",
    type: "PDFTextField",
    description: "Automatically uses the current date",
    icon: <TodayIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_full_title",
    field_label: "Full Title",
    field_type: "full_title",
    type: "PDFTextField",
    description: "Reusable field for a full title or heading",
    icon: <TitleIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_full_subtitle",
    field_label: "Full Sub Title",
    field_type: "full_subTitle",
    type: "PDFTextField",
    description: "Reusable field for a full subtitle or subheading",
    icon: <SubtitlesIcon fontSize="small" color="primary" />,
  },
  {
    field_id: "generic_total",
    field_label: "Total",
    field_type: "total",
    type: "PDFTextField",
    description: "Reusable field for displaying a calculated total",
    icon: <FunctionsIcon fontSize="small" color="primary" />,
  },
];

const GenericFormFieldPanel = ({
  currentProcessingFile,
  setCurrentProcessingFile: setCurrentProcessingFileRaw,
  onNotify,
  loading: loadingDoc = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFieldIds, setExpandedFieldIds] = useState(() => new Set());

  const setCurrentProcessingFile = useCallback(
    (update) => {
      setCurrentProcessingFileRaw((previousFile) => {
        const nextFile =
          typeof update === "function" ? update(previousFile) : update;

        return cleanupMultilineConnectionsAfterRetag(previousFile, nextFile);
      });
    },
    [setCurrentProcessingFileRaw],
  );

  const filteredFields = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return GENERIC_FORM_FIELDS;
    return GENERIC_FORM_FIELDS.filter(
      (f) =>
        f.field_label.toLowerCase().includes(q) ||
        f.field_type.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q),
    );
  }, [searchQuery]);

  const handleAccordionChange = (fieldId) => (event, isExpanded) => {
    setExpandedFieldIds((previousIds) => {
      const nextIds = new Set(previousIds);

      if (isExpanded) {
        nextIds.add(fieldId);
      } else {
        nextIds.delete(fieldId);
      }

      return nextIds;
    });
  };

  const handleAttach = (e, field) => {
    e.stopPropagation();

    const tempFields = currentProcessingFile?.document?.tempFields;

    if (!tempFields?.selectedExtraFields?.length) {
      onNotify?.(
        "error",
        "Select a field from the PDF first, then you'll be able to assign it.",
      );
      return;
    }

    if (tempFields?.selectedExtraFields?.length > 1) {
      onNotify?.(
        "error",
        "You have selected more than one field. Only a single field can be assigned at a time.",
      );
      return;
    }

    const selectedField = tempFields?.selectedExtraFields?.[0] ?? {};

    const updatedField = {
      ...selectedField,

      field_label: selectedField?.field_label,
      label: selectedField?.label || selectedField?.field_label,
      field_id: field.field_id,
      field_type: field.field_type,
      is_generic: true,
      location: "create",
      ...(field.type && { type: field.type }),
      ...(field.field_type === "percentageCheckbox" && { hidden: false }),
    };

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (selectedField?.name === f?.name) {
        return updatedField;
      }
      return f;
    });

    const updatedSelectedFields = [updatedField];

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: updatedFormFields,
          selectedFormField: updatedSelectedFields[0],
          selectedExtraFields: updatedSelectedFields,
        },
      },
    }));

    onNotify?.("success", `Field tagged as generic ${field.field_label}`);
  };

  const createExtraFieldName = () => {
    if (typeof crypto !== "undefined" && crypto?.randomUUID) {
      return `extra_field_${crypto.randomUUID()}`;
    }

    return `extra_field_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const handleAddAsExtraField = (e, field) => {
    e.stopPropagation();

    if (!field?.field_id) {
      onNotify?.("error", "Cannot add field: missing field id.");
      return;
    }

    const tempFields = currentProcessingFile?.document?.tempFields;
    if (!tempFields) return;

    // Block: field is already tagged/assigned anywhere in formFields
    const alreadyTagged = tempFields.formFields?.some(
      (f) => f?.field_id === field.field_id && f?.hidden === true,
    );

    if (alreadyTagged) {
      onNotify?.(
        "error",
        `"${field.field_label ?? field.field_name}" is already tagged as extra form field.`,
      );
      return;
    }

    const newExtraField = {
      name: createExtraFieldName(),
      field_label: field.field_label ?? field.field_name,
      field_id: field.field_id,
      field_type: field.field_type,
      location: field.location || "create",
      hidden: true, // makes it show up in Extra Form Fields drawer
      block_id: "",
      block_label: "",
    };

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: [
            ...(prevFile.document.tempFields.formFields || []),
            newExtraField,
          ],
        },
      },
    }));

    onNotify?.(
      "success",
      `"${field.field_label ?? field.field_name}" added as extra form field.`,
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        height: "calc(100% - 98px)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.5, pb: 1.25, mt: 1.25 }}>
        <TextField
          id="genericSearchInput"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          placeholder="Search generic fields..."
          fullWidth
          sx={{
            "& .MuiInputBase-input": { fontSize: 14 },
          }}
          InputProps={{
            sx: { padding: "0px 10px" },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0, // Prevents flexbox overflow cut-off issues
          overflowY: "auto",
          px: 1.5,
        }}
      >
        <Stack gap={0.75}>
          {filteredFields.map((field) => {
            const isExpanded = expandedFieldIds.has(field.field_id);
            return (
              <Accordion
                key={field.field_id}
                expanded={isExpanded}
                onChange={handleAccordionChange(field.field_id)}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px !important",
                  overflow: "hidden",
                  borderLeft: "3px solid #2e7d32",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": {
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <AccordionSummary
                  component="div"
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.2rem" }} />}
                  sx={{
                    gap: 1.5,
                    minHeight: "36px !important",
                  }}
                  slotProps={{
                    content: {
                      sx: {
                        display: "flex",
                        flexDirection: "column",
                        my: "8px !important",
                      },
                    },
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.85rem",
                          color: "#1F2937",
                          fontWeight: 600,
                        }}
                      >
                        {field.field_label}
                      </Typography>

                      {/* Chips Row */}
                      <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={0.5}
                        sx={{ mt: 0.5, minHeight: 20 }}
                      >
                        <Chip
                          label="Generic"
                          size="small"
                          variant="filled"
                          color="success"
                          sx={{
                            fontSize: "0.7rem",
                            height: 20,
                            borderRadius: 1,
                          }}
                        />
                        {!isExpanded && (
                          <Chip
                            label={startCase(field.field_type)}
                            size="small"
                            variant="filled"
                            sx={{
                              fontSize: "0.7rem",
                              height: 20,
                              borderRadius: 1,
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => handleAttach(e, field)}
                        sx={{
                          border: "1px solid #d0d7de",
                          borderRadius: 1.5,
                          backgroundColor: "transparent",
                          p: "4.41px !important",
                        }}
                        disabled={loadingDoc}
                      >
                        <AttachFileIcon
                          sx={{
                            fontSize: "0.95rem",
                            transform: "rotate(45deg)",
                          }}
                        />
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0.5, pb: 1, px: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.75,
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={startCase(field.field_type)}
                        size="small"
                        variant="filled"
                        sx={{
                          fontSize: "0.7rem",
                          height: 20,
                          borderRadius: 1,
                        }}
                      />
                    </Box>

                    {/* RIGHT SIDE — ONLY ExtraFieldIcon */}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      justifyContent="flex-end"
                    >
                      <Tooltip
                        title="Add as extra field"
                        arrow
                        placement="bottom"
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleAddAsExtraField(e, field)}
                          sx={{
                            border: "1px solid #d0d7de",
                            borderRadius: 1.5,
                            backgroundColor: "transparent",
                            p: "4.41px !important",
                          }}
                          disabled={loadingDoc}
                        >
                          <ExtraFieldIcon
                            sx={{
                              fontSize: "0.95rem",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {filteredFields.length === 0 && (
            <Typography
              variant="body2"
              align="center"
              sx={{ color: "text.secondary", mt: 4 }}
            >
              No generic fields matching "{searchQuery}"
            </Typography>
          )}
        </Stack>
      </Box>
      <Box sx={{ height: 40, flexShrink: 0 }} />
    </Box>
  );
};

export default GenericFormFieldPanel;
